import type { ReactNode, JSX } from 'react'

import { Badge, Text } from '@buffer-mono/popcorn'

import { useRelativeTime } from './hooks/useRelativeTime'
import styles from './EngagementHeader.module.css'

type EngagementHeaderProps = {
  /**
   * Id for the header element. Referenced by the engagement row's
   * `aria-labelledby`, so it must match what the surrounding `EngagementItem`
   * points at.
   */
  id?: string
  /**
   * Author identity node — the pre-resolved display name. Passed as a node so
   * callers can wrap it (e.g. a private-account popover) without the header
   * needing to know about it.
   */
  username: ReactNode
  /** ISO timestamp of publication; rendered as a relative "x ago" label. */
  timestamp?: string | null
  /**
   * Pre-resolved status label (e.g. "Replied" / "Resolved"). Omit to hide the
   * badge — the header never sees the underlying status enum.
   */
  statusLabel?: string
  /**
   * Inline actions rendered after the status, inside the identity row
   * (e.g. a "Create a Post" button).
   */
  actions?: ReactNode
  /**
   * Trailing content rendered in the header's second column, outside the
   * identity row (e.g. reactions).
   */
  trailing?: ReactNode
}

/**
 * Identity row shared by comment and mention rows: display name + relative
 * timestamp, with an optional status badge, inline actions, and trailing
 * content. Renders the semantic `<header>` landmark itself, so callers drop it
 * straight into an `EngagementItem` content slot without wrapping.
 */
export const EngagementHeader = ({
  id,
  username,
  timestamp,
  statusLabel,
  actions,
  trailing,
}: EngagementHeaderProps): JSX.Element => {
  const relativeTimestamp = useRelativeTime(timestamp)

  return (
    <header id={id} className={styles.header}>
      <div className={styles.headerContent}>
        <span className={styles.username}>{username}</span>
        {relativeTimestamp && (
          <Text size="sm" color="subtle">
            {relativeTimestamp}
          </Text>
        )}
        {statusLabel && (
          <Badge variant="neutral" size="small">
            {statusLabel}
          </Badge>
        )}
        {actions && <span className={styles.actions}>{actions}</span>}
      </div>
      {trailing}
    </header>
  )
}

EngagementHeader.displayName = 'EngagementHeader'
